<template>
  <el-dialog
    v-model="dialogVisible"
    width="60%"
    destroy-on-close
  >
    <Container
      :title="t('pay.batchOutsideConfirm')"
    >
      <template #main>
        <el-row style="height: 360px; overflow: auto;">
          <SimpleTable
            ref="tableRef"
            :tableData="tableData"
            :columnStruct="columnStruct"
            :hadOperate="false"
          />
        </el-row>
        <el-row>
          <div style="text-align: left;margin-top: 10px;display: grid; grid-auto-flow: column; gap: 10px">
            <i
              style="font-size: 18px; color:#ff4949"
              class="el-icon-warning"
            />
            <br>
            <span style="padding-bottom: 15px;">{{ t('pay.vitroPayTip') }}</span>
          </div>
        </el-row>
        <el-row>
          <div style="margin-bottom: 10px">
            {{ t('pay.totalAmountPayable') }}: {{ totalAmount }}
          </div>
        </el-row>
        <el-row>
          <CenterForm
            ref="cFormRef"
            :formModel="batchPayFormModel"
            :formStruct="batchPayFormStruct"
            :formRules="batchPayFormRules"
            :labelWidth="'100px'"
            :showButton="false"
          />
        </el-row>
      </template>
    </Container>
    <template #footer>
      <el-button @click="handleCancel">
        {{ t('message.cancel') }}
      </el-button>
      <el-button
        type="primary"
        @click="handleConfirmPay"
      >
        {{ t('pay.confirmPay') }}
      </el-button>
    </template>
  </el-dialog>
</template>

<script lang="ts">

import { computed, defineComponent, onMounted, reactive, ref, toRefs } from 'vue'
import { useI18n } from 'vue-i18n'
import Container from '@/components/form/container/Container.vue'
import { PaymentExternalBatchUpdateModel, PaymentSlipModel } from '@/model/paymentSlip/paymentSlipModel'
import { formatDate, formatThousands } from '@/utils'
import { CompanyListParamModel } from '@/model/master-data/companyModel'
import { getCompanyAllList } from '@/apis/main-data/company'
import SimpleTable from '@/components/table/simple/SimpleTable.vue'
import CenterForm from '@/components/form/common/CenterForm.vue'
import { createModel } from '@/utils/component'
import { batchExternalPayment } from '@/apis/paymentSlip/paymentSlip'
import { ElMessage } from 'element-plus'

export default defineComponent({
  name: 'BatchVitroPayDialog',
  methods: { formatThousands },
  props: {
    modelValue: {
      type: Boolean,
      required: true
    },
    selectRowList: {
      type: Array as () => PaymentSlipModel[],
      required: true,
      default: []
    },
    labelWidth: {
      type: String,
      default: '680px'
    }
  },
  components: { CenterForm, SimpleTable, Container },
  emits: ['update:modelValue', 'refresh'],
  setup(props, context) {
    const { t } = useI18n()
    const cFormRef = ref()
    // 使用 computed 实现双向绑定
    const dialogVisible = computed({
      get: () => props.modelValue,
      set: (value) => context.emit('update:modelValue', value)
    })
    const paymentSlipTableStruct = [
      { prop: 'id', label: t('pay.id') },
      { prop: 'companyCode', label: t('company.companyCode') },
      { prop: 'payee', label: t('pay.payee') },
      { prop: 'reference', label: t('pay.reference') },
      { prop: 'amount', label: t('pay.amount'), formatter: (row: any) => formatThousands(row.amount), align: 'right' },
      { prop: 'currency', label: t('pay.currency') },
      { prop: 'dueDate', label: t('order.cutOffDate'), formatter: (row: any) => formatDate(row.dueDate, 'yyyy-MM-dd') },
      { prop: 'comments', label: t('pay.comments') },
      {
        prop: 'paymentDate',
        label: t('pay.paymentDate'),
        formatter: (row: any) => formatDate(row.paymentDate, 'yyyy-MM-dd')
      },
      { prop: 'purpose', label: t('pay.purposeView'), width: 170 }
    ]
    const batchPayFormStruct = [
      {
        prop: 'companyCode',
        label: t('company.companyCode'),
        component: 'el-select',
        type: 'text',
        model: '',
        loading: false,
        options: [] as any
      },
      {
        prop: 'paymentDate',
        label: t('pay.paymentDate'),
        component: 'el-date-picker',
        type: 'date',
        model: ''
      },
      {
        prop: 'purpose',
        label: t('pay.purpose'),
        component: 'el-input',
        type: 'text',
        model: ''
      },
      {
        prop: 'comments',
        label: t('pay.comment'),
        component: 'el-input',
        type: 'text',
        model: ''
      }
    ]
    const batchPayFormRules = {
      companyCode: [{ required: true, message: t('company.companyCodeP'), trigger: 'blur' }],
      paymentDate: [{ required: true, message: t('pay.paymentDateP'), trigger: 'blur' }],
      purpose: [{ required: true, message: t('approvalForm.purposeP'), trigger: 'blur' }]
    }
    const dataMap = reactive({
      tableData: Array<PaymentSlipModel>(),
      columnStruct: paymentSlipTableStruct,
      batchPayFormModel: createModel(batchPayFormStruct) as any,
      batchPayFormStruct: batchPayFormStruct,
      batchPayFormRules: batchPayFormRules,
      totalAmount: 0,
      async getCompanyCodes() {
        dataMap.batchPayFormStruct[0].loading = true
        const params: CompanyListParamModel = {
          companyCode: null,
          name: null
        }
        const data = await getCompanyAllList(params)
        const list = data?.data ?? []
        const optionList = []
        for (const iterator of list) {
          optionList.push({
            label: iterator.companyCode + ', ' + iterator.name,
            value: iterator.companyCode
          })
        }
        dataMap.batchPayFormStruct[0].options = optionList
        dataMap.batchPayFormStruct[0].loading = false
      },
      async handleConfirmPay() {
        const mainValid = await cFormRef.value?.validate().catch(() => false)
        if (!mainValid) {
          return
        }
        const idList = dataMap.tableData.map(item => item.id)
        const params: PaymentExternalBatchUpdateModel = {
          idList: idList,
          companyCode: dataMap.batchPayFormModel.companyCode,
          paymentDate: formatDate(dataMap.batchPayFormModel.paymentDate),
          purpose: dataMap.batchPayFormModel.purpose,
          comments: dataMap.batchPayFormModel.comments
        }
        const res = await batchExternalPayment(params)
        if (res?.data && res?.data > 0) {
          ElMessage({
            message: t('pay.batchOutsideConfirmSuccess'),
            type: 'success',
            duration: 2000
          })
          context.emit('refresh')
          dialogVisible.value = false
        } else {
          ElMessage({
            message: res?.msg,
            type: 'error',
            duration: 2000
          })
        }
      },
      handleCancel() {
        dialogVisible.value = false
      }
    })
    onMounted(() => {
      dataMap.tableData = props.selectRowList
      for (let i = 0; i < props.selectRowList.length; i++) {
        dataMap.totalAmount += props.selectRowList[i].amount
      }
      dataMap.getCompanyCodes()
    })
    return {
      t,
      cFormRef,
      dialogVisible,
      ...toRefs(dataMap)
    }
  }
})
</script>

<style
  scoped
  lang="scss"
>
.header-title {
  font-weight: bold;
  font-size: 16px;
  color: rgba(0, 0, 0, 0.85);
}

.button-group {
  display: flex;
  justify-content: flex-end;

  .order-button {
    margin-left: 10px;
  }
}

</style>
